import { useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useAuth } from "../../../auth/store";
import { ErrorState } from "../../../components/States";
import { DataTable, PageHeader } from "../../../components/ui";
import { useWidth } from "../../insights/charts/useWidth";
import { backendApi, backendKeys, type MapEdge } from "../api";
import { BackendActions, BackendTabs, ConnectPrompt, ms, pct, useBackendRange } from "../components";

type NodeKind = "browser" | "service" | "database";

interface Placed {
  id: string;
  kind: NodeKind;
  col: number;
  row: number;
}

const COL_W = 220;
const ROW_H = 72;
const NODE_W = 168;
const NODE_H = 40;
const V_ROW_H = 92;

/** Columns by call depth: browsers first, databases last, each service one step past its deepest caller. */
export function layout(edges: MapEdge[]): { nodes: Placed[]; cols: number; rows: number } {
  const kind = new Map<string, NodeKind>();
  for (const e of edges) {
    if (e.kind === "browser") kind.set(e.from, "browser");
    if (e.kind === "database") kind.set(e.to, kind.get(e.to) ?? "database");
    if (!kind.has(e.from)) kind.set(e.from, "service");
    if (!kind.has(e.to)) kind.set(e.to, "service");
  }
  const depth = new Map<string, number>();
  for (const id of kind.keys()) depth.set(id, kind.get(id) === "browser" ? 0 : 1);
  // Relax a bounded number of times so a cycle cannot loop forever.
  for (let i = 0; i < kind.size; i++) {
    let changed = false;
    for (const e of edges) {
      if (kind.get(e.to) === "database") continue;
      const d = (depth.get(e.from) ?? 0) + 1;
      if (d > (depth.get(e.to) ?? 0) && d <= kind.size) {
        depth.set(e.to, d);
        changed = true;
      }
    }
    if (!changed) break;
  }
  const maxService = Math.max(1, ...[...kind.entries()].filter(([, k]) => k !== "database").map(([id]) => depth.get(id) ?? 1));
  for (const [id, k] of kind) if (k === "database") depth.set(id, maxService + 1);
  const byCol = new Map<number, string[]>();
  for (const [id, d] of depth) byCol.set(d, [...(byCol.get(d) ?? []), id]);
  const usedCols = [...byCol.keys()].sort((a, b) => a - b);
  const nodes: Placed[] = [];
  usedCols.forEach((c, ci) => {
    (byCol.get(c) ?? []).sort().forEach((id, row) => nodes.push({ id, kind: kind.get(id)!, col: ci, row }));
  });
  return { nodes, cols: usedCols.length, rows: Math.max(1, ...[...byCol.values()].map((v) => v.length)) };
}

const KIND_LABEL: Record<NodeKind, string> = { browser: "Browser", service: "Service", database: "Database" };

export function MapPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [range] = useBackendRange();
  const q = useQuery({ queryKey: backendKeys.map(auth.currentProjectId, range), queryFn: () => backendApi.map(range), enabled: !!auth.currentProjectId });
  const edges = useMemo(() => q.data?.edges ?? [], [q.data]);
  const { nodes, cols, rows } = useMemo(() => layout(edges), [edges]);
  const [hover, setHover] = useState<string | null>(null);
  const box = useRef<HTMLDivElement>(null);
  const avail = useWidth(box, 900) - 24;
  // Narrow screens draw the map top to bottom, with each step's services side by side.
  const vertical = avail < 560;
  const nodeW = vertical ? Math.max(96, Math.min(NODE_W, Math.floor((avail - 32 - (rows - 1) * 12) / rows))) : NODE_W;
  const pos = new Map(nodes.map((n) => [n.id, n]));
  const width = vertical ? Math.max(avail, rows * (nodeW + 12) + 20) : Math.max(cols * COL_W, COL_W);
  const height = vertical ? (cols - 1) * V_ROW_H + NODE_H + 32 : rows * ROW_H + 16;
  const at = (n: Placed) => (vertical ? { x: 16 + n.row * (nodeW + 12), y: 16 + n.col * V_ROW_H } : { x: n.col * COL_W + 16, y: n.row * ROW_H + 16 });
  const maxChars = Math.floor((nodeW - 24) / 7);
  const edgePath = (a: Placed, b: Placed) => {
    const p = at(a);
    const r = at(b);
    if (vertical) {
      const x1 = p.x + nodeW / 2, y1 = p.y + NODE_H, x2 = r.x + nodeW / 2, y2 = r.y;
      if (y2 > y1) return `M${x1},${y1} C${x1},${(y1 + y2) / 2} ${x2},${(y1 + y2) / 2} ${x2},${y2}`;
      return `M${p.x + nodeW},${p.y + NODE_H / 2} C${p.x + nodeW + 40},${p.y + NODE_H / 2} ${r.x + nodeW + 40},${r.y + NODE_H / 2} ${r.x + nodeW},${r.y + NODE_H / 2}`;
    }
    const x1 = p.x + nodeW, y1 = p.y + NODE_H / 2, x2 = r.x, y2 = r.y + NODE_H / 2, mid = (x1 + x2) / 2;
    if (x2 > x1) return `M${x1},${y1} C${mid},${y1} ${mid},${y2} ${x2},${y2}`;
    return `M${p.x + nodeW / 2},${p.y + NODE_H} C${p.x + nodeW / 2},${y1 + 60} ${r.x + nodeW / 2},${y2 + 60} ${r.x + nodeW / 2},${r.y + NODE_H}`;
  };

  return (
    <>
      <PageHeader title="Backend" actions={<BackendActions />}>
        <BackendTabs />
      </PageHeader>
      {q.isError ? (
        <div className="card section"><ErrorState error={q.error} retry={() => void q.refetch()} /></div>
      ) : !q.isPending && edges.length === 0 ? (
        <ConnectPrompt title="Nothing to map yet" hint="Once two services, or a service and its database, talk to each other, the map draws who calls whom." />
      ) : (
        <>
          <div className="card be-map" ref={box} aria-hidden={q.isPending || undefined}>
            {q.isPending ? (
              <div className="skeleton be-chart-skeleton">loading</div>
            ) : (
              <svg width={width} height={height} role="img" aria-label="Map of services and what they call">
                {edges.map((e, i) => {
                  const a = pos.get(e.from);
                  const b = pos.get(e.to);
                  if (!a || !b) return null;
                  const lit = hover === e.from || hover === e.to;
                  const bad = e.calls > 0 && e.errors / e.calls >= 0.05;
                  return (
                    <path
                      key={i}
                      d={edgePath(a, b)}
                      className={`be-map-edge${bad ? " bad" : ""}${lit ? " lit" : ""}`}
                      strokeWidth={Math.min(1 + Math.log10(e.calls + 1), 4)}
                    >
                      <title>{`${e.from} → ${e.to}: ${e.calls.toLocaleString()} calls, ${pct(e.errors, e.calls)} errors, ${ms(e.avgMs)} average`}</title>
                    </path>
                  );
                })}
                {nodes.map((n) => {
                  const c = at(n);
                  const opens = n.kind === "service";
                  return (
                    <g
                      key={n.id}
                      transform={`translate(${c.x},${c.y})`}
                      className={`be-map-node ${n.kind}${opens ? " opens" : ""}`}
                      onMouseEnter={() => setHover(n.id)}
                      onMouseLeave={() => setHover(null)}
                      onClick={opens ? () => void navigate({ to: "/backend/services/$service" as never, params: { service: n.id } as never }) : undefined}
                    >
                      <title>{n.id}</title>
                      <rect width={nodeW} height={NODE_H} rx={8} />
                      <text x={12} y={17} className="be-map-kind">{KIND_LABEL[n.kind]}</text>
                      <text x={12} y={32} className="be-map-name">{n.id.length > maxChars ? `${n.id.slice(0, maxChars - 1)}…` : n.id}</text>
                    </g>
                  );
                })}
              </svg>
            )}
          </div>
          <div className="card table-scroll stack-table be-table">
            <DataTable<MapEdge>
              ariaLabel="Connections"
              rows={[...edges].sort((a, b) => b.calls - a.calls)}
              rowKey={(e) => `${e.from}->${e.to}:${e.kind}`}
              loading={q.isPending}
              columns={[
                { key: "from", header: "From", label: "From", render: (e) => <strong>{e.from}</strong> },
                { key: "to", header: "To", label: "To", render: (e) => <span>{e.to}{e.kind === "database" ? <span className="muted small"> · database</span> : null}</span> },
                { key: "calls", header: "Calls", label: "Calls", align: "right", render: (e) => <span className="mono">{e.calls.toLocaleString()}</span> },
                { key: "err", header: "Errors", label: "Errors", align: "right", render: (e) => <span className="mono">{pct(e.errors, e.calls)}</span> },
                { key: "avg", header: "Average", label: "Average", align: "right", render: (e) => <span className="mono">{ms(e.avgMs)}</span> },
              ]}
            />
          </div>
        </>
      )}
    </>
  );
}
